import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { subscriptionTeamQuery, subscriptionTeamQueryVariables } from '../generated/subscriptionTeamQuery';
import { subscriptionTeamFieldsFragment } from '../models/SubscriptionTeam';
import { mainApiConfig } from '../../../configure/mainApiConfig';

/**
 * Get a SubscriptionTeam from the store.
 * @param id
 * @param options
 */
export function useSubscriptionTeam(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<subscriptionTeamQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<subscriptionTeamQuery, subscriptionTeamQueryVariables>(
        gql`
        query subscriptionTeamQuery ($id: ID!) {
            model: subscriptionTeam(id: $id) {
                ...subscriptionTeamFields
            }
        }

        ${subscriptionTeamFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
